import { Formik, FieldArray, Field, Form } from 'formik';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Modal,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  Button,
  Input,
  VStack,
  FormLabel,
  Text,
  Tooltip,
  Heading,
  ModalFooter,
  ModalButtonGroup,
  Divider,
} from '@frontend/components';

import { isValidEmail } from 'utils/stringUtils';

import { sendInvitesModal, sendInvites } from './data/actions';
import { getIsSendInvitesModalOpen, getIsFetching } from './data/selectors';

interface FormValues {
  emails: (string | undefined)[];
}

interface FormErrors {
  [key: string]: string;
}

export const SendInvitesModal = () => {
  const dispatch = useDispatch();
  const isFetching = useSelector(getIsFetching);
  const isOpen = useSelector(getIsSendInvitesModalOpen);
  const [error, setError] = useState<string | undefined>(undefined);

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => dispatch(sendInvitesModal.close())}
      size="md"
    >
      <ModalContent>
        <ModalCloseButton />
        <Formik<FormValues>
          initialValues={{
            emails: [undefined, undefined, undefined],
          }}
          onSubmit={({ emails }) => {
            const emailAddresses = emails.filter(
              (emailAddress) => emailAddress,
            );
            if (emailAddresses.length > 0) {
              dispatch(sendInvites.request({ emailAddresses }));
            } else {
              setError(
                'To invite team members, enter at least one valid email.',
              );
            }
          }}
        >
          {(formState) => {
            const { values, errors } = formState;
            const hasValidEmail = values.emails.some(
              (email) => email && isValidEmail(email),
            );
            const hasInvalidEmail = values.emails.some(
              (email) => email && !isValidEmail(email),
            );
            const errorMessage = hasInvalidEmail
              ? 'To invite team members, all emails must be valid'
              : 'To invite team members, enter at least one valid email.';
            return (
              <Form>
                <ModalBody px={4} pt={6}>
                  <VStack spacing={1} align="center">
                    <img
                      width="88px"
                      height="77px"
                      src="https://s3.amazonaws.com/static.paperlessparts.com/images/invite-icon.png"
                      alt="invite icon"
                    />
                    <Heading>Invite your team</Heading>
                    <Text textAlign="center" size="lg">
                      Paperless Parts works best when your whole team is
                      together.
                      <br />
                      Easily share files, tag teammates, and collaborate.
                    </Text>
                    <Divider my={2} />
                    <FieldArray name="emails">
                      {(arrayHelpers) => (
                        <VStack align="stretch" width={40}>
                          <FormLabel>Invite Teammates</FormLabel>
                          <VStack align="stretch">
                            {values.emails.map((email, index) => {
                              const fieldName = `email${index}`;
                              return (
                                <Field
                                  key={fieldName}
                                  component={Input}
                                  autoComplete="none"
                                  placeholder="name@example.com"
                                  name={fieldName}
                                  onChange={(
                                    e: React.ChangeEvent<HTMLInputElement>,
                                  ) => {
                                    error && setError(undefined);
                                    arrayHelpers.replace(index, e.target.value);
                                  }}
                                  validate={() => {
                                    return (
                                      values.emails[index] &&
                                      !isValidEmail(values.emails[index]) &&
                                      'Please enter a valid email.'
                                    );
                                  }}
                                  value={email}
                                  isInvalid={
                                    !!(errors as FormErrors)[fieldName]
                                  }
                                  error={(errors as FormErrors)[fieldName]}
                                />
                              );
                            })}
                          </VStack>
                          <Button
                            variant="link"
                            alignSelf="flex-start"
                            onClick={() => arrayHelpers.push(undefined)}
                          >
                            Add another email
                          </Button>
                        </VStack>
                      )}
                    </FieldArray>
                  </VStack>
                </ModalBody>
                <ModalFooter justifyContent="center" border="none" p={4}>
                  <ModalButtonGroup>
                    <Button
                      variant="secondary"
                      type="button"
                      onClick={() => dispatch(sendInvitesModal.close())}
                    >
                      Skip
                    </Button>
                    <Tooltip variant="expanded" label={errorMessage}>
                      <Button
                        isDisabled={!hasValidEmail}
                        variant="primary"
                        isLoading={isFetching}
                        type="submit"
                      >
                        Send Invitations
                      </Button>
                    </Tooltip>
                  </ModalButtonGroup>
                </ModalFooter>
              </Form>
            );
          }}
        </Formik>
      </ModalContent>
    </Modal>
  );
};
